// KeepPileReview Component
// Full-screen modal for reviewing kept cards

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeepPileEntry } from '../../../types/swipe';
import { PERSONALITY_SUITS } from '../../../constants/swipe';
import {
  KeepPileOverlay,
  KeepPileModal,
  ModalHeader,
  CloseKeepPileReviewLink,
  KeepPileGrid,
  KeptCard,
  KeptCardImage,
  KeptCardName,
  KeptCardCompatibility,
  KeptCardActions,
  KeptCardButton,
  KeepPileFooter,
  ContinueSwipeButton,
  GoToMatchingButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  EmptyStateButton,
  ExpirationTimer
} from '../../../styles/Swipe/KeepPile.styles';

interface KeepPileReviewProps {
  entries: KeepPileEntry[];
  /**
   * How many cards this account may hold, from its subscription tier.
   *
   * Was written into the heading as a literal 5, which is the FREE allowance.
   * A Premium Plus member holding fourteen of his twenty was told "14/5" — a
   * number that reads as broken, and quietly misprices the plan he is paying
   * for. The default matches the free tier only as a last resort, for the
   * moment before the allowance loads.
   */
  maxSize?: number;
  onClose: () => void;
  onRemove: (entryId: string) => void;
  onViewProfile: (profileId: string) => void;
  onGoToMatching: (entry: KeepPileEntry) => void;
}

export const KeepPileReview: React.FC<KeepPileReviewProps> = ({
  entries,
  maxSize = 5,
  onClose,
  onRemove,
  onViewProfile,
  onGoToMatching
}) => {
  const navigate = useNavigate();

  // Lock the page behind while this is open.
  //
  // overscroll-behavior on the modal stops a scroll that starts INSIDE it from
  // chaining outward, but it does nothing about a touch that begins on the
  // backdrop or on a gap in the layout — that still reaches the page. Both
  // halves are needed, which is why the other full-screen modals in this app
  // (the swipe modal, the onboarding modal) do the same thing.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Calculate time remaining for a card
  const getTimeRemaining = (expiresAt: string): string => {
    const now = new Date().getTime();
    const expires = new Date(expiresAt).getTime();
    const remaining = expires - now;

    if (remaining <= 0) return 'Expired';

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 48) {
      const days = Math.floor(hours / 24);
      return `${days}d left`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    }
    return `${minutes}m left`;
  };

  // Get card suit color - use stored suit from swipe card, fallback to personality mapping
  const getSuitColor = (entry: KeepPileEntry): string => {
    // If cardSuit is stored, use it (matches the swipe card)
    if (entry.cardSuit) {
      return PERSONALITY_SUITS[entry.cardSuit].colorTheme;
    }

    // Fallback to personality type mapping for older entries
    const personalityType = entry.profile.personalityType || 'The Romantic';
    const suitMapping: Record<string, 'hearts' | 'diamonds' | 'clubs' | 'spades'> = {
      'The Romantic': 'hearts',
      'The Adventurer': 'spades',
      'The Thinker': 'clubs',
      'The Entrepreneur': 'diamonds',
      'The Creative': 'spades',
      'The Socialite': 'clubs',
      'The Caregiver': 'hearts',
      'The Leader': 'diamonds'
    };
    const suit = suitMapping[personalityType] || 'hearts';
    return PERSONALITY_SUITS[suit].colorTheme;
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Render empty state
  if (entries.length === 0) {
    return (
      <KeepPileOverlay onClick={handleBackdropClick}>
        <KeepPileModal>
          <ModalHeader>
            <div>
              <h2>Your Keep Pile</h2>
              <CloseKeepPileReviewLink onClick={onClose}>
                Close Keep Pile Review
              </CloseKeepPileReviewLink>
            </div>
          </ModalHeader>

          <EmptyState>
            <EmptyStateIcon>🃏</EmptyStateIcon>
            <EmptyStateTitle>Your keep pile is empty</EmptyStateTitle>
            <EmptyStateText>
              Keep profiles you're interested in to add them to your keep pile.
              You can save up to 5 profiles.
            </EmptyStateText>
            <EmptyStateButton onClick={onClose}>
              Start Swiping
            </EmptyStateButton>
          </EmptyState>
        </KeepPileModal>
      </KeepPileOverlay>
    );
  }

  return (
    <KeepPileOverlay onClick={handleBackdropClick}>
      <KeepPileModal>
        <ModalHeader>
          <div>
            <h2>Your Keep Pile ({entries.length}/{maxSize})</h2>
            <CloseKeepPileReviewLink onClick={onClose}>
              Close Keep Pile Review
            </CloseKeepPileReviewLink>
          </div>
        </ModalHeader>

        {/* Cards grid */}
        <KeepPileGrid>
          {entries.map((entry) => {
            const timeRemaining = getTimeRemaining(entry.expiresAt);
            const isExpiringSoon = new Date(entry.expiresAt).getTime() - Date.now() < 24 * 60 * 60 * 1000;
            const suitColor = getSuitColor(entry);

            return (
              <KeptCard key={entry.id} $suitColor={suitColor}>
                <KeptCardImage
                  src={entry.profile.photos[0]}
                  alt={entry.profile.firstName}
                />

                <KeptCardName>
                  {entry.profile.firstName}, {entry.profile.age}
                </KeptCardName>

                <KeptCardCompatibility>
                  {entry.profile.compatibilityScore}% Match
                </KeptCardCompatibility>

                <ExpirationTimer $isExpiring={isExpiringSoon}>
                  ⏰ {timeRemaining}
                </ExpirationTimer>

                <KeptCardActions>
                  <KeptCardButton
                    $variant="secondary"
                    onClick={() => onViewProfile(entry.profileId)}
                  >
                    View
                  </KeptCardButton>
                  <KeptCardButton
                    $variant="primary"
                    onClick={() => onGoToMatching(entry)}
                  >
                    Play
                  </KeptCardButton>
                  <KeptCardButton
                    $variant="danger"
                    onClick={() => onRemove(entry.id)}
                  >
                    ✕
                  </KeptCardButton>
                </KeptCardActions>
              </KeptCard>
            );
          })}
        </KeepPileGrid>

        {/* Footer */}
        <KeepPileFooter>
          <ContinueSwipeButton onClick={onClose}>
            Continue Swiping
          </ContinueSwipeButton>
          <GoToMatchingButton
            disabled={entries.length === 0}
            onClick={() => {
              if (entries.length > 0) {
                onGoToMatching(entries[0]);
              }
            }}
          >
            🎯 Start Matching ({entries.length})
          </GoToMatchingButton>
        </KeepPileFooter>
      </KeepPileModal>
    </KeepPileOverlay>
  );
};

